/**
 * The /scan kiosk's press bar — THE tap target for a per-copy press, shared by the Classic tile and the
 * Press-mode rail so the two layouts cannot drift on what a tap means. Extracted verbatim from
 * `PlacementTile`; the handler, its guards and the money path behind it are the page's, unchanged.
 *
 * Three renderings, decided by the placement, not by the layout:
 *  · screen-print → informational (pressed at the screen-print worklist, never here);
 *  · DTF on an actionable scan → the toggle (tap to press, tap the green state to un-press);
 *  · DTF on a non-actionable scan → a muted state line.
 *
 * `disarmed` is Press-mode's auto-advance guard: for a moment after the view slides to the next side, the
 * bar — which sits in the same place on every side — refuses a tap, so a double-tap habit cannot press
 * the side that just arrived under the finger. Display-only: it only withholds the handler.
 */
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function PressBar({
  method,
  pressed,
  pressable,
  pending,
  disarmed = false,
  onToggle,
}: {
  method: 'dtf' | 'screen-print';
  pressed: boolean;
  pressable: boolean;
  pending: boolean;
  disarmed?: boolean;
  onToggle: () => void;
}) {
  if (method === 'screen-print') {
    return (
      <div className="flex-none border-t px-4 py-4 text-center text-sm text-muted-foreground">
        {pressed ? '✓ Screen-printed' : 'Pressed at the screen-print station'}
      </div>
    );
  }
  if (!pressable) {
    return (
      <div className="flex-none border-t px-4 py-4 text-center text-sm text-muted-foreground">
        {pressed ? '✓ Pressed' : '—'}
      </div>
    );
  }
  // Toggle: tap to press, tap the green state to un-press (the scope-keyed reverse). The bar — not the
  // art — is the tap target, so handling the garment over the preview can't mint a press.
  return (
    <button
      type="button"
      disabled={pending || disarmed}
      onClick={onToggle}
      aria-pressed={pressed}
      className={cn(
        'flex min-h-[76px] w-full flex-none items-center justify-center gap-3 border-t text-xl font-extrabold transition-colors',
        pressed
          ? 'border-emerald-500 bg-emerald-600 text-white'
          : 'border-emerald-800 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-950/70',
      )}
    >
      <span
        className={cn(
          'flex size-8 items-center justify-center rounded-md border-2',
          pressed ? 'border-white/70 bg-white/20' : 'border-emerald-500',
        )}
      >
        {pending ? (
          <Loader2 className="size-5 animate-spin" />
        ) : pressed ? (
          <Check className="size-6" />
        ) : null}
      </span>
      {pressed ? 'Pressed' : 'Mark pressed'}
    </button>
  );
}
